import React, {FC, SyntheticEvent, useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface CustomEmojiInputProps {
  onChange: HandlerOf<File | undefined>;
}

/*
 * Style.
 */

const StyledFieldLabel = styled.label`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledImageUpload = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-top: 8px;
  margin-bottom: 20px;

  span {
    margin-top: 4px;
    margin-bottom: 8px;
    font-size: ${fontSizes.veryTiny};
  }

  input {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    visibility: hidden;
  }
`;

const StyledImageInput = styled.div<{$size: number}>`
  padding: 8px;
  border-radius: 8px;
  background-color: ${(p) => p.theme.greys.shade30};
  width: fit-content;
  height: fit-content;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  margin-right: 12px;
`;

const StyledImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

/*
 * Component.
 */

export const CustomEmojiInput: FC<CustomEmojiInputProps> = ({onChange}) => {
  const theme = useUserTheme();
  const [file, setFile] = useState<File>();
  const [imageSrc, setImageSrc] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onFileLoaded = useCallback(
    (event: ProgressEvent<FileReader>) => {
      setImageSrc(event.target?.result?.toString() ?? '');
    },
    [setImageSrc],
  );

  useEffect(() => {
    onChange(file);
    if (!file) {
      setImageSrc('');
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = onFileLoaded;
    fileReader.readAsDataURL(file);
  }, [file, onChange, onFileLoaded]);

  const onFileInputChange = useCallback(
    (event: SyntheticEvent<HTMLInputElement>) => {
      const {files} = event.currentTarget;
      const newFile = files && files[0];
      if (!newFile) {
        return;
      }
      setFile(newFile);
    },
    [setFile],
  );

  return (
    <>
      <StyledFieldLabel>
        <Trans id="custom-emoji-image-field-name">Image upload</Trans>
      </StyledFieldLabel>
      <StyledImageUpload>
        <StyledImageInput $size={56} onClick={() => fileInputRef.current?.click()}>
          {imageSrc ? (
            <StyledImage src={imageSrc} alt="" />
          ) : (
            <Icon size={40} color={theme.greys.shade60} name="formattingImage" />
          )}
        </StyledImageInput>
        <span>
          <Trans id="custom-emoji-upload-instructions">Drag and drop JPG/PNG/GIF up to 512KB</Trans>
        </span>
        <Button onClick={() => fileInputRef.current?.click()}>
          <Trans id="custom-emoji-upload-button">Upload Image</Trans>
        </Button>
        <input
          id="custom-emoji-add-input"
          type="file"
          ref={fileInputRef}
          onChange={onFileInputChange}
          accept=".png, .jpg, .gif"
        />
      </StyledImageUpload>
    </>
  );
};
